import { request } from './client'

/* PHOTOGRAPHS — of a fault, and of finished work.

   THE ORDERING RULE THAT SHAPES EVERY CALLER: the report is never held hostage to the
   photograph. A fault is raised first and photographed second, as two requests, so an
   attendant in a basement with no signal still gets the fault into the system. A fault
   with no photograph is far better than no fault; the reverse is not true, and a single
   combined request would make the photograph able to lose both. */

/* CHECKLIST_ANSWER is a photograph the TEMPLATE asked for, of one item of one clean.
   Separate from TASK because a clean requiring a bathroom photograph and a balcony
   photograph is not satisfied by two photographs of the bathroom — and against the
   task alone the two are indistinguishable. */
export type AttachmentSubject = 'ISSUE' | 'TASK' | 'CHECKLIST_ANSWER'

export interface Attachment {
  fileObjectId: string
  subject: AttachmentSubject
  subjectId: string
  contentType: string
  sizeBytes: number
  /** What the device called it. A LABEL — the server never used it as a path. */
  originalFileName: string | null
  uploadedByUserId: string
  uploadedByName: string
  uploadedByNameAr: string | null
  uploadedAtUtc: string
  /** When the CAMERA took it, if the device said. Different from uploadedAtUtc whenever
   *  the photograph waited for a signal — which in a basement is always. */
  capturedAtUtc: string | null
}

/** Eight megabytes, matching the server. Duplicated deliberately: the handset must be
 *  able to say "too big" without a round trip, and a constant that disagrees with the
 *  server is caught by a contract test rather than by an attendant. */
export const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024
export const MAX_ATTACHMENTS_PER_SUBJECT = 6

export const ALLOWED_ATTACHMENT_TYPES = [
  'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif',
] as const

export function fetchAttachments(
  propertyId: string,
  subject: AttachmentSubject,
  subjectId: string,
  options: { signal?: AbortSignal } = {},
): Promise<Attachment[]> {
  return request<Attachment[]>(
    `/properties/${propertyId}/attachments/${subject}/${subjectId}`,
    options.signal ? { signal: options.signal } : {})
}

export function uploadAttachment(
  propertyId: string,
  subject: AttachmentSubject,
  subjectId: string,
  photo: {
    /** The bytes. A Blob in a browser; on the handset, whatever the camera hands back. */
    data: BodyInit
    contentType: string
    fileName?: string
    /** The camera's own clock, ISO-8601. */
    capturedAtUtc?: string
  },
  options: { signal?: AbortSignal } = {},
): Promise<Attachment> {
  return request<Attachment>(
    `/properties/${propertyId}/attachments/${subject}/${subjectId}`,
    {
      method: 'POST',
      rawBody: photo.data,
      contentType: photo.contentType,
      headers: {
        // Headers, not query parameters. A file name and a timestamp in a URL end up in
        // access logs and in a browser's history, and one of the two is user-supplied text.
        ...(photo.fileName ? { 'X-Housekeeping-File-Name': photo.fileName } : {}),
        ...(photo.capturedAtUtc ? { 'X-Housekeeping-Captured-At': photo.capturedAtUtc } : {}),
      },
      ...(options.signal ? { signal: options.signal } : {}),
    })
}

/** Where the bytes are. Needs the session, so it is fetched rather than put in an <img src>. */
export function attachmentUrl(apiBaseUrl: string, fileObjectId: string): string {
  return `${apiBaseUrl}/attachments/${fileObjectId}`
}

/** Records a removal. NOT a DELETE: nothing is destroyed — the row and the bytes both
 *  stay, and what is written is who removed it and why. */
export function removeAttachment(fileObjectId: string, reason: string): Promise<void> {
  return request<void>(`/attachments/${fileObjectId}/removal`, {
    method: 'POST',
    body: { reason },
  })
}

/**
 * FETCHES THE BYTES AND HANDS BACK AN OBJECT URL.
 *
 * `<img src="/api/v1/attachments/{id}">` CANNOT WORK, and it is worth saying why rather
 * than letting somebody try it: the download needs the `Authorization` header, and a
 * browser sends none on an image request. The alternatives are all worse — a token in
 * the query string ends up in access logs and browser history, and a cookie would mean
 * giving this API a second, weaker way to authenticate.
 *
 * THE CALLER MUST REVOKE THE URL. Six photographs held open on a supervisor's board all
 * morning is six full-size bitmaps that never leave memory.
 */
export async function fetchAttachmentBlobUrl(
  fileObjectId: string,
  options: { signal?: AbortSignal } = {},
): Promise<string> {
  const blob = await request<Blob>(`/attachments/${fileObjectId}`, {
    ...(options.signal ? { signal: options.signal } : {}),
    expect: 'blob',
  })
  return URL.createObjectURL(blob)
}
